import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Loader2, Sparkles, Download, Copy, Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

export default function ResumeBuilder() {
    const [emphasis, setEmphasis] = useState("");
    const [tone, setTone] = useState("confident");
    const [current, setCurrent] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/professional/ai/resume/history");
            setHistory(data);
            if (data.length > 0 && !current) setCurrent(data[0]);
        } catch (err) {
            // Non-critical background load — history is a nice-to-have, not
            // blocking. Log for devs but don't toast (would spam on every
            // transient network hiccup while the user is doing something else).
            console.warn("[ResumeBuilder] loadHistory failed:", err?.message || err);
        }
    };
    // One-shot load on mount — loadHistory doesn't need to be in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadHistory(); }, []);

    const generate = async () => {
        try {
            setLoading(true);
            const { data } = await api.post("/professional/ai/resume", { emphasis, tone });
            setCurrent(data);
            toast.success("Resume generated.");
            loadHistory();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    const copy = () => {
        if (!current?.markdown) return;
        navigator.clipboard.writeText(current.markdown);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const download = () => {
        if (!current?.markdown) return;
        const blob = new Blob([current.markdown], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "resume.md"; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="resume-builder-page">
            <div className="mb-6">
                <p className="overline">AI Career Assistant</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Resume Builder
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Simonara AI writes an ATS-friendly Markdown resume tailored to your Passport.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
                <div className="card-soft p-5 h-fit lg:sticky lg:top-24">
                    <div className="text-sm text-muted-foreground mb-3">Personalize the output.</div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Emphasis / focus role</label>
                    <Input value={emphasis} onChange={(e) => setEmphasis(e.target.value)} placeholder="e.g. Emphasize leadership + AI experience" className="mt-1 mb-3" data-testid="rb-emphasis" />
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tone</label>
                    <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger className="mt-1 mb-4" data-testid="rb-tone"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="confident">Confident</SelectItem>
                            <SelectItem value="warm">Warm</SelectItem>
                            <SelectItem value="concise">Concise</SelectItem>
                        </SelectContent>
                    </Select>
                    <button onClick={generate} disabled={loading} className="btn-green w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="rb-generate">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate resume
                    </button>

                    {history.length > 0 && (
                        <div className="mt-6">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">History</div>
                            <div className="space-y-1">
                                {history.map((h) => (
                                    <button key={h.id} onClick={() => setCurrent(h)} className={`w-full text-left rounded-lg p-2 text-xs border transition-colors ${current?.id === h.id ? "border-emerald-500/50 bg-emerald-500/5" : "border-border hover:border-emerald-500/30"}`} data-testid={`rb-history-${h.id}`}>
                                        <div className="text-foreground truncate">{h.summary || "Resume"}</div>
                                        <div className="text-[10px] text-muted-foreground">{new Date(h.created_at).toLocaleString()}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    {!current ? (
                        <div className="card-soft p-12 text-center">
                            <FileText size={22} className="mx-auto text-muted-foreground" />
                            <div className="mt-2 font-heading font-semibold">No resume generated yet.</div>
                            <div className="mt-1 text-sm text-muted-foreground">Set your emphasis and tone, then click Generate.</div>
                        </div>
                    ) : (
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                            {current.summary && (
                                <div className="card-soft p-4 bg-emerald-500/5 border-emerald-500/30">
                                    <div className="overline text-emerald-600">Tagline</div>
                                    <div className="mt-1 text-sm text-foreground">{current.summary}</div>
                                </div>
                            )}
                            <div className="card-soft p-5">
                                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                    <div className="font-heading font-semibold text-foreground">Your resume</div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={copy} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="rb-copy">
                                            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                                        </button>
                                        <button onClick={download} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="rb-download">
                                            <Download size={12} /> Download .md
                                        </button>
                                    </div>
                                </div>
                                <Textarea readOnly value={current.markdown} rows={18} className="font-mono text-xs" data-testid="rb-markdown" />
                            </div>
                            {(current.strengths?.length > 0 || current.gaps?.length > 0) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {current.strengths?.length > 0 && (
                                        <div className="card-soft p-4">
                                            <div className="overline text-emerald-600">Strengths</div>
                                            <ul className="mt-2 space-y-1">
                                                {current.strengths.map((s, i) => <li key={i} className="text-xs text-foreground">• {s}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {current.gaps?.length > 0 && (
                                        <div className="card-soft p-4">
                                            <div className="overline text-amber-600">Suggestions</div>
                                            <ul className="mt-2 space-y-1">
                                                {current.gaps.map((s, i) => <li key={i} className="text-xs text-foreground">• {s}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
}
